import React from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { useTheme } from '../theme/ThemeContext';
import { getUserFacingMessage } from '../utils/errors';
import { useNetworkStore } from '../stores/networkStore';

interface DataStateViewProps {
  /** True during initial load (from TanStack Query's isLoading). */
  isLoading: boolean;
  /** The error object, if any (from TanStack Query's error). */
  error: Error | null;
  /** Whether cached data exists (i.e. `data` is truthy). */
  hasData: boolean;
  /** Retry/refetch function. */
  onRetry: () => void;
  /** Label for the entity being loaded (e.g. "visions", "goals"). */
  entityName: string;
  /** testID prefix for retry button (e.g. "visions"). */
  testIDPrefix?: string;
  /** The content to render when data is available. */
  children: React.ReactNode;
}

/**
 * Handles the 4 pre-content data states for list screens:
 *
 * 1. Loading (first load, no cache) → spinner
 * 2. Error (no cache) → error message + retry
 * 3. Offline, no cache → "connect to load" message
 * 4. Data available (fresh or stale) → renders children
 *
 * When data IS available, always renders children (even if stale or erroring).
 * The LastUpdatedBanner should be rendered separately inside the children.
 */
export default function DataStateView({
  isLoading,
  error,
  hasData,
  onRetry,
  entityName,
  testIDPrefix,
  children,
}: DataStateViewProps) {
  const { colors } = useTheme();
  const connectivityState = useNetworkStore((s) => s.connectivityState);

  // If we have cached data, always show it (children handle stale banner)
  if (hasData) return <>{children}</>;

  // First load — spinner
  if (isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.loadingText, { color: colors.textMuted }]}>
          Loading {entityName}...
        </Text>
      </View>
    );
  }

  // Offline, no cached data
  if (connectivityState !== 'online' && !error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.icon}>{'\u{1F4F6}'}</Text>
        <Text style={[styles.messageText, { color: colors.textSecondary }]}>
          {connectivityState === 'offline'
            ? 'You are offline. Connect to the internet to load data.'
            : 'Server is temporarily unavailable. Please try again later.'}
        </Text>
        <TouchableOpacity
          style={[styles.retryButton, { backgroundColor: colors.primary }]}
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel={`Retry loading ${entityName}`}
          testID={testIDPrefix ? `${testIDPrefix}.retry-button` : undefined}
        >
          <Text style={[styles.retryText, { color: colors.background }]}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Error with no cached data
  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.icon}>{'\u26A0'}</Text>
        <Text style={[styles.messageText, { color: colors.error }]}>
          {getUserFacingMessage(error)}
        </Text>
        <TouchableOpacity
          style={[styles.retryButton, { backgroundColor: colors.primary }]}
          onPress={onRetry}
          accessibilityRole="button"
          accessibilityLabel={`Retry loading ${entityName}`}
          testID={testIDPrefix ? `${testIDPrefix}.retry-button` : undefined}
        >
          <Text style={[styles.retryText, { color: colors.background }]}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Fallback (shouldn't happen)
  return <>{children}</>;
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
  },
  icon: {
    fontSize: 48,
    marginBottom: 12,
  },
  messageText: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 22,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryText: {
    fontWeight: '600',
    fontSize: 15,
  },
});
